import React, { RefObject, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { ImThemeProviderZenith } from '@im/im-theme';
import { MuiProviders } from '@im/ui-lib/theme/MuiProviders';
import { DialogFooter } from '@im/ui-lib/zenith/DialogFooter';
import { useBillingAddressByCNPJ } from '../../../hooks/use-billing-address-by-cnpj';
import {
  SelectionGroup,
  SelectionGroupProps,
} from '@im/ui-lib/zenith/SelectionGroup';
import { useBillingHeaderProvider } from 'mf_core/provider';
import { CustomerBillingSelector } from '../../../app/component/ResellerBillingSelector/internal/CustomerBillingSelector';
import { BillingCard } from '../../../app/component/ResellerBillingSelector/internal/BillingCard';
import { useTranslate } from '@im/react-localization';

type Props = {
  defaultAddress: any;
  getFormattedAddress: any;
  isMyBusiness: boolean;
  onCancelClick: () => void;
  onPrimaryClick: (myBusiness: boolean, selectedAddress: any) => void;
  shipToAddresses: any;
};

export const BillingModeSelectorContent = (Props: Props) => {
  const {
    defaultAddress,
    getFormattedAddress,
    isMyBusiness,
    onCancelClick,
    onPrimaryClick,
    shipToAddresses,
  } = Props;
  const translate = useTranslate();
  const [inputValue, setInputValue] = useState('');
  const [enableAddressByCNPJ, setEnableAddressByCNPJ] = useState(false);
  const { billingHeader } = useBillingHeaderProvider();

  const [inputErrorMessage, setInputErrorMessage] = useState<string>('');
  const {
    data: addressByCNPJ,
    error,
    isError,
    isLoading,
    isSuccess,
  } = useBillingAddressByCNPJ(
    inputValue,
    Boolean(inputValue) && !inputErrorMessage && enableAddressByCNPJ
  );
  const [customerAddressByCNPJ, setCustomerAddressByCNPJ] = useState<any>();
  const [selectionGroupData, setSelectionGroupData] = useState<
    SelectionGroupProps[]
  >([]);
  const [selectedRadioValue, setSelectedRadioValue] = useState(
    defaultAddress?.taxExemptNumber ?? ''
  );
  const [selectedAddress, setSelectedAddress] = useState<any>({});

  useEffect(() => {
    if (!isLoading && (isSuccess || isError)) {
      setCustomerAddressByCNPJ(isSuccess ? { ...addressByCNPJ?.[0] } : {});

      if (isError && error) {
        setInputErrorMessage(
          'This CNPJ doesn’t exist. Check for errors and try again.'
        );
      }
      setEnableAddressByCNPJ(false);
    }
  }, [isLoading, isError, addressByCNPJ, error, isSuccess]);

  const onBillingCardCancel = () => {
    setCustomerAddressByCNPJ({});
    setSelectedAddress({});
    setInputValue('');
  };

  useEffect(() => {
    if (!isMyBusiness) {
      const cusTab = billingHeader?.selectedMode === 'customer';
      setCustomerAddressByCNPJ(
        cusTab ? billingHeader?.billingAddress?.[0] : {}
      );
      setSelectedAddress(cusTab ? billingHeader?.billingAddress?.[0] : {});
    } else {
      setInputValue('');
      const tab = billingHeader?.selectedMode !== 'business';
      setSelectedAddress(
        tab ? defaultAddress : billingHeader?.billingAddress?.[0]
      );
      setSelectedRadioValue(
        tab ? defaultAddress?.taxExemptNumber : billingHeader?.cnpjNumber
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyBusiness]);

  const handleChangeRadio = (radioRef: RefObject<HTMLInputElement>) => {
    if (radioRef.current) {
      setSelectedRadioValue(radioRef.current.value);

      const address = shipToAddresses?.filter(
        (f: any) => f.taxExemptNumber === radioRef?.current?.value
      )[0];
      setSelectedAddress(address);
    }
  };

  const getSelectedStatus = (value: string) => {
    return selectedRadioValue === value;
  };

  useEffect(() => {
    setSelectionGroupData(
      shipToAddresses?.map((address: any) => ({
        headerText: 'CNPJ',
        subHeaderText: address?.taxExemptNumber,
        summary: getFormattedAddress(address),
        value: address?.taxExemptNumber,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipToAddresses]);

  useEffect(() => {
    if (customerAddressByCNPJ?.taxExemptNumber) {
      setSelectedAddress(customerAddressByCNPJ);
    }
  }, [customerAddressByCNPJ]);

  const setAddress = (address: any) => {
    setSelectedAddress(address);
  };

  return (
    <ImThemeProviderZenith>
      <MuiProviders>
        <Stack>
          {isMyBusiness ? (
            <Stack
              px={4}
              py={2}
              spacing={2}
              sx={{
                maxHeight: '250px',
                overflowX: 'hidden',
                overflowY: 'auto',
              }}
            >
              <Typography variant="text-sm-medium">
                {' '}
                {selectionGroupData?.length > 1
                  ? translate('BillingHeader.YourShipTos')
                  : translate('BillingHeader.YourShipTo')}
              </Typography>
              {selectionGroupData?.length > 1
                ? selectionGroupData?.map((address: any, index: number) => (
                    <SelectionGroup
                      convertTxtToBlue={getSelectedStatus}
                      isSelected={getSelectedStatus(address.value)}
                      key={address.value}
                      onChangeRadio={handleChangeRadio}
                      selectionType="single-select"
                      size="sm"
                      type="Radio button"
                      {...address}
                    />
                  ))
                : selectionGroupData && (
                    <BillingCard
                      isCancelRequired={false}
                      onBillingCardCancel={onBillingCardCancel}
                      subHeaderText={
                        selectionGroupData?.[0]?.subHeaderText ?? ''
                      }
                      summary={selectionGroupData?.[0]?.summary ?? ''}
                    />
                  )}
            </Stack>
          ) : (
            <CustomerBillingSelector
              isType1={true}
              selectedAddress={customerAddressByCNPJ}
              setSelectedAddress={setAddress}
            />
          )}
          <DialogFooter
            divider
            onPrimaryButtonClick={() => {
              onPrimaryClick(
                isMyBusiness,
                isMyBusiness && shipToAddresses?.length <= 1
                  ? shipToAddresses?.[0]
                  : selectedAddress
              );
            }}
            onSecondaryButtonClick={onCancelClick}
            primaryButtonProps={{
              disabled: !isMyBusiness && !selectedAddress?.taxExemptNumber,
            }}
            primaryButtonText={translate('BillingHeader.Confirm')}
            secondaryButtonText={translate('BillingHeader.Cancel')}
            type="horizontal-fill-container"
          />
        </Stack>
      </MuiProviders>
    </ImThemeProviderZenith>
  );
};
